
import { useState, useRef } from "react";

export interface PixelStateCallbacks {
  onSubmitStateChange?: (isSubmitting: boolean) => void;
  onInstallationPhaseChange?: (isInstallation: boolean) => void;
}

export function usePixelState({ onSubmitStateChange, onInstallationPhaseChange }: PixelStateCallbacks = {}) {
  // Core state
  const [pixelId, setPixelId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [installationPhase, setInstallationPhase] = useState(false);
  
  // Refs for tracking state transitions
  const pixelCreatedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stateTransitionRef = useRef<{
    isSettingPixelId: boolean;
    lastOperation: string;
    timestamp: number;
  }>({
    isSettingPixelId: false,
    lastOperation: "",
    timestamp: 0,
  });

  // Internal state setters with logging
  const setSubmittingState = (value: boolean) => {
    setIsSubmitting(value);
    if (onSubmitStateChange) {
      onSubmitStateChange(value);
    }
  };

  const setInstallationState = (value: boolean) => {
    setInstallationPhase(value);
    if (onInstallationPhaseChange) {
      onInstallationPhaseChange(value);
    }
  };

  const updatePixelId = (id: string | null) => {
    stateTransitionRef.current = {
      isSettingPixelId: true,
      lastOperation: id ? "set_pixel_id" : "clear_pixel_id",
      timestamp: Date.now()
    };
    setPixelId(id);
  };

  const resetState = () => {
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Order is important: first disable phases, then clear ID
    setSubmittingState(false);
    setInstallationState(false);
    pixelCreatedRef.current = false;
    
    // Finally update the ID (this should be last)
    stateTransitionRef.current = {
      isSettingPixelId: false,
      lastOperation: "reset_pixel_id",
      timestamp: Date.now()
    };
    updatePixelId(null);
  };

  // Explicitly expose a method to fully reset state for reuse
  const completeReset = () => {
    resetState();
    
    // Ensure all refs are reset too
    pixelCreatedRef.current = false;
    stateTransitionRef.current = {
      isSettingPixelId: false,
      lastOperation: "complete_reset",
      timestamp: Date.now()
    };
  };

  return {
    // State
    pixelId,
    isSubmitting,
    installationPhase,
    
    // Refs
    pixelCreatedRef,
    timeoutRef,
    stateTransitionRef,
    
    // Setters
    setPixelId: updatePixelId,
    setSubmittingState,
    setInstallationState,
    resetState,
    completeReset
  };
}
